import { useMemo } from "react"
import { ScatterChart } from "@mui/x-charts"

import type { RecordType, ScatterPlotView } from "types/dashboard.types"
import type { FieldQualityMap } from "types/quality.types"

import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import useMuiScatterConfig from "widgets/plots/configuration/useMuiScatterConfig"
import MuiReferenceLines from "../MuiReferenceLines"
import MuiChartZoom from "../zoom/MuiChartZoom"
import CrosshairLine from "../CrosshairLine"
import ChartHoverBridge from "../ChartHoverBridge"
import TooltipPositionBridge from "../TooltipPositionBridge"
import { getStyles } from "./styles"
import MuiScatterTooltip from "./MuiScatterTooltip"

interface MuiScatterChartProps {
  view: ScatterPlotView
  data: RecordType[]
  fromDate?: number
  toDate?: number
  fieldQualityMap?: FieldQualityMap
}

const MuiScatterChart = ({
  view,
  data,
  fromDate,
  toDate,
  fieldQualityMap,
}: MuiScatterChartProps) => {
  const { entityType: contextEntity } = useDashboardContext()
  const config = useMuiScatterConfig({ view, data, fromDate, toDate, fieldQualityMap })
  const {
    series,
    xAxis,
    yAxis,
    margin,
    xReferenceLines,
    yReferenceLines,
    legend,
    tooltip,
    grid,
    xDomainMin,
    xDomainMax,
    marker,
  } = config
  const lineSx = useMemo(() => getStyles(config), [config])
  const slotProps = useMemo(
    () => ({ tooltip: { ...tooltip, fieldQualityMap } }),
    [tooltip, fieldQualityMap],
  )
  const slots = useMemo(() => ({ tooltip: MuiScatterTooltip, marker }), [marker])

  return (
    <>
      {series && (
        <ScatterChart
          key={`scatter-${xDomainMin}-${xDomainMax}`}
          skipAnimation
          voronoiMaxRadius={30}
          hideLegend={legend?.hidden}
          margin={margin}
          xAxis={xAxis}
          yAxis={yAxis}
          series={series}
          grid={grid}
          sx={lineSx}
          slotProps={slotProps}
          slots={slots}
        >
          <TooltipPositionBridge />
          <MuiReferenceLines
            xReferenceLines={xReferenceLines}
            yReferenceLines={yReferenceLines}
          />
          {contextEntity && (
            <CrosshairLine xDomainMin={xDomainMin} xDomainMax={xDomainMax} />
          )}
          {contextEntity && (
            <MuiChartZoom
              entity={contextEntity}
              xDomainMin={xDomainMin}
              xDomainMax={xDomainMax}
            />
          )}
          {contextEntity && (
            <ChartHoverBridge xDomainMin={xDomainMin} xDomainMax={xDomainMax} />
          )}
        </ScatterChart>
      )}
    </>
  )
}

export default MuiScatterChart
